import {
	ReactElement,
	useMemo
} from "react";
import { DocumentLocationModelKind } from "client/Api/Hub/hubSettings";
import {
	Anchor,
	Button,
	Icon
} from "client/Components";
import { getSharePointLocationUrl, getWebDavFolderUrl } from "client/Features/Documents/routing";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import styles from "./SelectedLocation.module.less";
import { DocumentLocationFormData } from "./HubSettings";
import { setTrackingId } from "client/Misc/utils";

export type SelectedLocationProps = {
	documentLocation: DocumentLocationFormData;
	onChange: () => void;
	onRemove: () => void;
};

export const SelectedLocation = (props: SelectedLocationProps): ReactElement => {
	const {
		documentLocation,
		onChange,
		onRemove
	} = props;

	const folderLink = useMemo(
		(): string => {
			switch (documentLocation.kind) {
				case DocumentLocationModelKind.SharePoint:
					const {
						itemId,
						listLocation
					} = documentLocation;

					return getSharePointLocationUrl(
						{
							listId: listLocation.listId,
							siteUrl: listLocation.siteUrl
						},
						itemId
					);
				case DocumentLocationModelKind.WebDav:
					const {
						fileId,
						serverId
					} = documentLocation;

					return getWebDavFolderUrl(serverId, fileId);
				default:
					never(documentLocation);
			}
		},
		[documentLocation]
	);

	const {
		kind,
		displayName
	} = documentLocation;

	return (
		<div
			{...setTrackingId("selected-location")}
			className={styles.root}
		>
			<Anchor
				className={styles.locationLink}
				aria-label={displayName}
				title={displayName}
				trackingId="document-location"
				href={folderLink}
				target="_blank"
			>
				<div className={styles.locationIcon}>
					<Icon
						value={{
							kind: "svg",
							name: "folder"
						}}
					/>
				</div>

				<Location
					kind={kind}
					displayName={displayName}
				/>
			</Anchor>

			<div className={styles.actions}>
				<Button
					trackingId="change"
					text={localize("_Change")}
					kind="secondary"
					onClick={onChange}
				/>

				<Button
					trackingId="remove"
					text={localize("_Remove")}
					kind="secondary"
					onClick={onRemove}
				/>
			</div>
		</div>
	);
};
SelectedLocation.displayName = "SelectedLocation";

const kindToLocalizationKeyMap: Record<DocumentLocationModelKind, LocalizationKey> = {
	[DocumentLocationModelKind.SharePoint]: "_SharePoint",
	[DocumentLocationModelKind.WebDav]: "_FileServer"
};

type LocationProps = {
	kind: DocumentLocationModelKind;
	displayName: string;
};

const Location = (props: LocationProps): ReactElement => {
	const {
		kind,
		displayName
	} = props;

	const segments = useMemo(
		(): readonly string[] => {
			const result: readonly string[] = displayName.split("/");

			if (result.length === 0) {
				return [];
			}

			if (result.length > 3) {
				const folderName: string = result[result.length - 1];

				return [
					...result.slice(0, 2),
					"...",
					folderName
				];
			}

			return result;
		},
		[displayName]
	);

	return (
		<div
			className={styles.location}
			style={{
				"--location-segments-count": segments.length + 1
			}}
		>
			<span className={styles.locationSegment}>
				{localize(kindToLocalizationKeyMap[kind])}
				{segments.length === 0 && (
					<Separator />
				)}
			</span>
			{segments.map(
				(x, id) => (
					<span
						key={id}
						className={styles.locationSegment}
					>
						<Separator />
						{x}
					</span>
				)
			)}
		</div>
	);
};
Location.displayName = "Location";

const Separator = (): ReactElement => {
	return (
		<>/</>
	);
};
Separator.displayName = "Separator";